import * as S from "./styles.ts";
import LinkComponent, { TLink } from "./Link";
import { useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import CardCta from "./CardCta";
import useClickOutside from "@/lib/helpers/hooks/useClickOutside.ts";
import useBreakpoint from "@/lib/helpers/hooks/useBreakpoint.ts";
import { IImageProps } from "@/lib/types/components";
import { useTypedSelector } from "@/redux/helpers/hooks.ts";
import { useRouter } from "@/lib/helpers/hooks/useRouter.ts";
import ChevronDown from "@template/icons/ChevronDown.tsx";

export type TCategory = {
  label: string;
  link?: string;
  links?: TLink[];
  featureBoxInfo?: {
    url: string;
    label: string;
    description: string;
    image: IImageProps;
  };
};

interface ICategoryComponentProps {
  category: TCategory;
  availableTrial?: boolean;
  altStyling?: boolean;
}

const usPhoneNumberLinks: TLink[] = [
  {
    url: "/phone-number/all-numbers",
    label: "All Phone Numbers",
    description: "Choose and buy local and non-geographic phone numbers.",
  },
  {
    url: "/phone-number/keep-my-number",
    label: "Transfer Your Phone Number",
    description:
      "Port-in your existing phone numbers to use with our phone system.",
  },
  {
    url: "/phone-number/929-new-york",
    label: "New York 929 Numbers",
    description:
      "Buy 929 phone numbers for New York City and surrounding areas.",
  },
  {
    url: "/phone-number/213-los-angeles",
    label: "Los Angeles 213 Numbers",
    description: "Buy 213 phone numbers for a local presence in Los Angeles.",
  },
  {
    url: "/phone-number/773-chicago",
    label: "Chicago 773 Numbers",
    description: "Buy 773 phone numbers for a local presence in Chicago.",
  },
  {
    url: "/phone-number/832-houston",
    label: "Houston 832 Numbers",
    description: "Buy 832 phone numbers for a local presence in Houston.",
  },
  {
    url: "/phone-number/628-san-francisco",
    label: "San Francisco 628 Numbers",
    description: "Buy 628 phone numbers for a local presence in San Francisco.",
  },
  {
    url: "/phone-number/857-boston",
    label: "Boston 857 Numbers",
    description: "Buy 857 phone numbers for a local presence in Boston.",
  },
  {
    url: "/phone-number/737-austin",
    label: "Austin 737 Numbers",
    description: "Buy 737 phone numbers for a local presence in Austin.",
  },
  {
    url: "/phone-number/international",
    label: "International Phone Numbers",
    description: "Buy international phone numbers for your business.",
  },
];

const CategoryComponent = ({
  category,
  availableTrial,
  altStyling,
}: ICategoryComponentProps) => {
  const router = useRouter();

  const currency = useTypedSelector(
    (state) => state.store.site_version.currency
  );
  const { check: isBig } = useBreakpoint("md");

  const isUs = currency === "USD";

  const [isOpen, setIsOpen] = useState(false);

  const wrapperRef = useRef<HTMLDivElement>(null);

  useClickOutside(wrapperRef, () => setIsOpen(false));
  const { featureBoxInfo } = category;
  const hasCta =
    featureBoxInfo !== undefined &&
    (featureBoxInfo?.label ||
      featureBoxInfo?.description ||
      featureBoxInfo?.image?.src);

  return (
    <S.CategoryWrapper
      ref={wrapperRef}
      onClick={() => {
        if (category.link) {
          router.push(category.link);
        } else {
          setIsOpen(!isOpen);
        }
      }}
      $isOpen={isOpen}
      $altStyling={altStyling}
    >
      <S.CategoryTrigger
        tabIndex={0}
        onKeyDown={(e) => {
          const key = e.key;
          const activationKeys = ["Enter", " "];
          if (!activationKeys.includes(key)) return;
          e.preventDefault();
          if (category.link) {
            router.push(category.link);
          } else {
            setIsOpen(!isOpen);
          }
        }}
      >
        {category.label}
        {category.links?.length > 0 && <ChevronDown width={16} />}
      </S.CategoryTrigger>
      <AnimatePresence>
        {isOpen && (
          <S.CategoryLinksWrapper
            as={motion.div}
            initial={
              !isBig
                ? {
                    opacity: 0,
                    height: 0,
                  }
                : {
                    opacity: 0,
                    transform: "translate(-50%, -5px)",
                  }
            }
            animate={
              !isBig
                ? {
                    opacity: 1,
                    height: "auto",
                  }
                : {
                    opacity: 1,
                    transform: "translate(-50%, 0px)",
                  }
            }
            exit={
              !isBig
                ? {
                    opacity: 0,
                    height: 0,
                  }
                : {
                    opacity: 0,
                    transform: "translate(-50%, -5px)",
                  }
            }
            transition={{ duration: 0.2 }}
          >
            <S.NavigationContainer>
              <S.CategoryPopupContainer $hasCta={!!hasCta}>
                <S.LinksContainer>
                  {(category.label === "Phone Numbers" && isUs
                    ? usPhoneNumberLinks
                    : category.links
                  )?.map((link, i) => (
                    <LinkComponent
                      link={link}
                      key={i}
                    />
                  ))}
                </S.LinksContainer>
                {hasCta && isBig && (
                  <CardCta
                    url={featureBoxInfo.url}
                    label={featureBoxInfo.label}
                    description={featureBoxInfo.description}
                    image_src={featureBoxInfo.image?.src}
                    image_alt={featureBoxInfo.image?.alt}
                  />
                )}
              </S.CategoryPopupContainer>
            </S.NavigationContainer>
          </S.CategoryLinksWrapper>
        )}
      </AnimatePresence>
    </S.CategoryWrapper>
  );
};

export default CategoryComponent;
